/**
 * Async thunks for workspace resource selection with auto-save
 *
 * These thunks handle:
 * - Auto-saving dirty resources before switching
 * - Updating selection state
 * - Loading resource data if needed
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import {
  setSelectedReading,
  setSelectedCanvas,
  setSelectedPaper,
  clearAllSelections,
  markResourceClean,
  loadRubric,
  setReadingHighlights,
  addHighlight,
  deleteHighlight,
  changeHighlightCategory,
  changeHighlightComment,
  updateHighlight,
  resetHighlights,
  setGradingView,
  clearGradingView,
  setReadingGradingViewData,
  setCanvasGradingViewData,
  setReadingContributorsCache,
  setCanvasContributorsCache,
  markAssessmentSaved,
  loadCanvasRubric,
  markCanvasAssessmentSaved,
  setCanvasData,
} from '../slices/workspaceSlice';
import {
  updatePaper,
  getReadingRubric, getAnnotations,
  createAnnotation, updateAnnotation, deleteAnnotation,
  getReadingContributors,
  updateReadingAssessment,
  getCanvasRubric,
  updateCanvasAssessment,
  getCanvasUserData,
  updateCanvasUserData,
  getCanvasContributors,
} from '@services/api';
import type { CanvasData } from '../../types/workspace';
import { annotationsToHighlights, highlightToCreateRequest } from '@services/annotationMapper';
import { ApiResult } from '../../types/errors';
import {
  makeDefaultRubricSettings,
  normalizeRubricSettings,
  type RubricSettings,
} from '../../shared/utils/defaultRubricSettings';
import type { IHighlight } from '@argument-studio/react-pdf-highlighter-with-categories';

/**
 * Stage 3.6: highlight persistence now happens per-mutation via the
 * save*Highlight thunks, so the legacy "save the whole reading_data
 * JSON" path is a no-op. Left in place to keep the resource-switch
 * thunk's signature stable; remove in a follow-up cleanup commit
 * once we're confident no other code path expects it.
 */
const saveReadingIfDirty = async (_state: RootState): Promise<ApiResult<void>> => {
  return { success: true, data: undefined };
};

/**
 * Helper to save the current canvas if dirty. Stage 6.7: writes to the
 * per-user canvas_user_data endpoint instead of the legacy shared
 * canvases.canvas_data column, mirroring how Stage 3.6 migrated
 * reading saves to the annotations table.
 */
const saveCanvasIfDirty = async (state: RootState): Promise<ApiResult<void>> => {
  if (state.view.isDemoMode) {
    return { success: true, data: undefined };
  }
  const { workspace } = state;

  if (!workspace.dirtyResources.canvas || !workspace.selectedCanvasId) {
    return { success: true, data: undefined };
  }

  const canvas = workspace.canvases.find(c => c.canvas_id === workspace.selectedCanvasId);
  if (!canvas?.canvas_data) {
    return { success: true, data: undefined };
  }

  const result = await updateCanvasUserData(
    workspace.workspace_id,
    workspace.selectedCanvasId,
    canvas.canvas_data
  );
  if (!result.success) return result;
  return { success: true, data: undefined };
};

/**
 * Helper to save the current paper if dirty
 */
const savePaperIfDirty = async (state: RootState): Promise<ApiResult<void>> => {
  if (state.view.isDemoMode) {
    return { success: true, data: undefined };
  }
  const { workspace } = state;

  if (!workspace.dirtyResources.paper || !workspace.selectedPaperId) {
    return { success: true, data: undefined };
  }

  const paper = workspace.papers.find(p => p.paper_id === workspace.selectedPaperId);
  if (!paper?.paper_data) {
    return { success: true, data: undefined };
  }

  return await updatePaper(
    workspace.workspace_id,
    workspace.selectedPaperId,
    paper.paper_data.editorState
  );
};

/**
 * Best-effort fetch of a reading's rubric (settings + expert highlights)
 * and dispatch into Redux. Silent on 403/404 — contributors who lack
 * read access just stay on defaults, and a missing rubric is a normal
 * "not yet created" state. JSON-parse failures fall back to defaults
 * rather than crashing the selection flow.
 */
// ============================================================================
// Annotation mutation thunks (Item 7, Stage 3.6)
//
// Each thunk dispatches the existing Redux reducer for an optimistic
// update, then fires the corresponding API call in the background.
// POC stance on failures: log and leave the Redux state in place — no
// rollback yet. Proper sync UX (status indicator + rollback or retry)
// is post-paper polish.
//
// All thunks pull workspace_id and selected_reading_id from state; the
// component just dispatches with the highlight payload it already had.
// ============================================================================

interface ReadingContext {
  workspaceId: string;
  readingId: string;
}

const readingContext = (state: RootState): ReadingContext | null => {
  const { workspace_id, selectedReadingId } = state.workspace;
  if (!workspace_id || !selectedReadingId) return null;
  return { workspaceId: workspace_id, readingId: selectedReadingId };
};

/**
 * Stage 3.7: when the owner is in grading view, mutation thunks no-op
 * with a console warning. The contributor's annotations are read-only
 * to the grader; UI elements should also be gated, but this is the
 * last line of defense so an accidental keyboard shortcut or stale
 * dispatch doesn't write to the wrong bucket.
 */
const isInGradingView = (state: RootState): boolean =>
  state.workspace.gradingView !== undefined;

const isDemoMode = (state: RootState): boolean =>
  state.view.isDemoMode === true;

export const saveAddHighlight = createAsyncThunk<
  void,
  IHighlight,
  { state: RootState }
>(
  'annotations/saveAddHighlight',
  async (highlight, { getState, dispatch }) => {
    if (isInGradingView(getState())) {
      console.warn('saveAddHighlight ignored: grading view is active (read-only)');
      return;
    }
    dispatch(addHighlight(highlight));
    if (isDemoMode(getState())) return;
    const ctx = readingContext(getState());
    if (!ctx) return;
    const result = await createAnnotation(
      ctx.workspaceId,
      ctx.readingId,
      highlightToCreateRequest(highlight)
    );
    if (!result.success) {
      console.error('saveAddHighlight failed:', result.error.message);
    }
  }
);

export const saveDeleteHighlight = createAsyncThunk<
  void,
  { id: string },
  { state: RootState }
>(
  'annotations/saveDeleteHighlight',
  async ({ id }, { getState, dispatch }) => {
    if (isInGradingView(getState())) {
      console.warn('saveDeleteHighlight ignored: grading view is active (read-only)');
      return;
    }
    dispatch(deleteHighlight({ id }));
    if (isDemoMode(getState())) return;
    const ctx = readingContext(getState());
    if (!ctx) return;
    const result = await deleteAnnotation(ctx.workspaceId, ctx.readingId, id);
    if (!result.success) {
      console.error('saveDeleteHighlight failed:', result.error.message);
    }
  }
);

export const saveChangeHighlightCategory = createAsyncThunk<
  void,
  { id: string; category: string },
  { state: RootState }
>(
  'annotations/saveChangeHighlightCategory',
  async ({ id, category }, { getState, dispatch }) => {
    if (isInGradingView(getState())) {
      console.warn('saveChangeHighlightCategory ignored: grading view is active');
      return;
    }
    dispatch(changeHighlightCategory({ id, category }));
    if (isDemoMode(getState())) return;
    const ctx = readingContext(getState());
    if (!ctx) return;
    const result = await updateAnnotation(ctx.workspaceId, ctx.readingId, id, {
      category_name: category,
    });
    if (!result.success) {
      console.error('saveChangeHighlightCategory failed:', result.error.message);
    }
  }
);

export const saveChangeHighlightComment = createAsyncThunk<
  void,
  { id: string; comment: string },
  { state: RootState }
>(
  'annotations/saveChangeHighlightComment',
  async ({ id, comment }, { getState, dispatch }) => {
    if (isInGradingView(getState())) {
      console.warn('saveChangeHighlightComment ignored: grading view is active');
      return;
    }
    dispatch(changeHighlightComment({ id, comment }));
    if (isDemoMode(getState())) return;
    const ctx = readingContext(getState());
    if (!ctx) return;
    const result = await updateAnnotation(ctx.workspaceId, ctx.readingId, id, {
      comment_text: comment,
    });
    if (!result.success) {
      console.error('saveChangeHighlightComment failed:', result.error.message);
    }
  }
);

export const saveUpdateHighlight = createAsyncThunk<
  void,
  {
    id: string;
    position: Partial<{ boundingRect: any; rects: any[]; pageNumber: number }>;
    content: Partial<{ text?: string; image?: string }>;
  },
  { state: RootState }
>(
  'annotations/saveUpdateHighlight',
  async (payload, { getState, dispatch }) => {
    if (isInGradingView(getState())) {
      console.warn('saveUpdateHighlight ignored: grading view is active');
      return;
    }
    dispatch(updateHighlight(payload));
    if (isDemoMode(getState())) return;
    const ctx = readingContext(getState());
    if (!ctx) return;
    // PATCH with whatever fields the caller is changing. Position is sent
    // as a JSON blob; content fields as their respective string columns.
    const body: {
      annotation_data?: string;
      text_content?: string | null;
      image_url?: string | null;
    } = {};
    if (payload.position) {
      // Persist the MERGED position from state, never the partial payload.
      // AreaHighlight move/resize calls through with only { boundingRect };
      // serializing that alone overwrites the stored position and strips
      // pageNumber + rects — which crashed the reader on reload and left
      // image annotations unsortable (no page to sort by).
      const merged = getState()
        .workspace.readings.find((r) => r.reading_id === ctx.readingId)
        ?.reading_data?.highlights.find((h) => h.id === payload.id);
      let position = merged?.position ?? payload.position;
      // An area drag/resize rect carries the authoritative page it landed
      // on (the fork stamps boundingRect.pageNumber, -1 when unknown).
      // Promote a valid one to the top level — this also self-heals rows
      // whose page was previously lost the next time they're nudged.
      const rectPage = payload.position.boundingRect?.pageNumber;
      if (typeof rectPage === "number" && rectPage >= 1) {
        position = { ...position, pageNumber: rectPage };
      }
      body.annotation_data = JSON.stringify(position);
    }
    if (payload.content?.text !== undefined) body.text_content = payload.content.text;
    if (payload.content?.image !== undefined) body.image_url = payload.content.image;
    const result = await updateAnnotation(ctx.workspaceId, ctx.readingId, payload.id, body);
    if (!result.success) {
      console.error('saveUpdateHighlight failed:', result.error.message);
    }
  }
);

export const saveResetHighlights = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'annotations/saveResetHighlights',
  async (_, { getState, dispatch }) => {
    if (isInGradingView(getState())) {
      console.warn('saveResetHighlights ignored: grading view is active');
      return;
    }
    // Capture the IDs to delete BEFORE the reducer wipes Redux.
    const state = getState();
    const reading = state.workspace.readings.find(
      (r) => r.reading_id === state.workspace.selectedReadingId
    );
    const ids = reading?.reading_data?.highlights.map((h) => h.id) ?? [];
    dispatch(resetHighlights());
    if (isDemoMode(state)) return;
    const ctx = readingContext(state);
    if (!ctx || ids.length === 0) return;
    // Parallel deletes — no bulk endpoint yet. Each failure logs
    // independently; we don't unwind because the user already sees
    // an empty reading.
    const results = await Promise.all(
      ids.map((id) => deleteAnnotation(ctx.workspaceId, ctx.readingId, id))
    );
    const failures = results.filter((r) => !r.success);
    if (failures.length > 0) {
      console.error(`saveResetHighlights: ${failures.length}/${ids.length} deletes failed`);
    }
  }
);

/**
 * Fetch the current user's annotations for a reading and replace the
 * Redux highlights array with them (Item 7, Stage 3.6). Best-effort —
 * a network failure leaves the reading with whatever was already there
 * (typically the empty array seeded by setReadings).
 */
const loadAnnotationsForReading = async (
  workspaceId: string,
  readingId: string,
  dispatch: (action: unknown) => unknown
): Promise<void> => {
  const result = await getAnnotations(workspaceId, readingId);
  if (!result.success) {
    console.error('Failed to load annotations:', result.error.message);
    return;
  }
  const highlights = annotationsToHighlights(result.data);
  dispatch(setReadingHighlights({ readingId, highlights }));
};

/**
 * Stage 6.7: best-effort fetch of the current user's per-user canvas
 * blob and dispatch into Redux. Parallels loadAnnotationsForReading.
 * Treats missing rows / null blobs as "fresh canvas" — we dispatch an
 * empty {rectangles, arrows, scale} so the canvas renders cleanly
 * instead of staying undefined (which downstream renderers tolerate
 * but selectors would have to guard against).
 */
const loadCanvasDataForCanvas = async (
  workspaceId: string,
  canvasId: string,
  dispatch: (action: unknown) => unknown
): Promise<void> => {
  const result = await getCanvasUserData(workspaceId, canvasId);
  if (!result.success) {
    console.error('Failed to load canvas user data:', result.error.message);
    return;
  }

  let canvasData: CanvasData | undefined;
  if (result.data?.canvas_data) {
    try {
      canvasData = JSON.parse(result.data.canvas_data) as CanvasData;
    } catch (err) {
      console.error('Canvas user_data JSON parse failed:', err);
    }
  }
  if (!canvasData) {
    canvasData = { rectangles: [], scale: 1 };
  }
  dispatch(setCanvasData({ canvasId, canvasData }));
};

/**
 * Stage 6.2: best-effort fetch of a canvas's rubric and dispatch into
 * Redux. Parses canvas_data jsonb (the expert snapshot) into the
 * CanvasData shape used by selectors and the assessCanvas util.
 */
const loadRubricForCanvas = async (
  workspaceId: string,
  canvasId: string,
  dispatch: (action: unknown) => unknown
): Promise<void> => {
  const result = await getCanvasRubric(workspaceId, canvasId);
  if (!result.success) {
    // 404-style "no rubric yet" surfaces as a backend null which is
    // result.success===true with data===null, not an error. Treat
    // genuine failures as silent and proceed with no rubric.
    console.error('Failed to load canvas rubric:', result.error.message);
    return;
  }
  let expertCanvas: CanvasData | null = null;
  if (result.data?.canvas_data) {
    try {
      expertCanvas = JSON.parse(result.data.canvas_data) as CanvasData;
    } catch (err) {
      console.error('Canvas rubric canvas_data JSON parse failed:', err);
    }
  }
  dispatch(loadCanvasRubric({ canvasId, expertCanvas }));
};

const loadRubricForReading = async (
  workspaceId: string,
  readingId: string,
  dispatch: (action: unknown) => unknown
): Promise<void> => {
  const result = await getReadingRubric(workspaceId, readingId);
  if (!result.success || !result.data) return;

  let settings: RubricSettings = makeDefaultRubricSettings();
  if (result.data.criteria) {
    try {
      // Stage 3.8: normalizer handles both the new shape (matchThreshold
      // top-level) and the pre-3.8 shape ({ overlap: { threshold } }).
      // Existing rubrics in the DB still have the old shape until the
      // next auto-save overwrites them.
      settings = normalizeRubricSettings(JSON.parse(result.data.criteria));
    } catch (err) {
      console.error('Rubric criteria JSON parse failed:', err);
    }
  }

  let expertHighlights: IHighlight[] = [];
  if (result.data.coords) {
    try {
      const parsed = JSON.parse(result.data.coords);
      if (Array.isArray(parsed)) expertHighlights = parsed as IHighlight[];
    } catch (err) {
      console.error('Rubric coords JSON parse failed:', err);
    }
  }

  dispatch(loadRubric({ readingId, settings, expertHighlights }));
};

/**
 * Select a reading and auto-save the previous resource if dirty
 */
export const selectReading = createAsyncThunk<
  string | null,
  string | null,
  { state: RootState }
>(
  'workspace/selectReading',
  async (readingId, { getState, dispatch }) => {
    const state = getState();

    // Save all dirty resources before switching
    const saveResults = await Promise.all([
      saveReadingIfDirty(state),
      saveCanvasIfDirty(state),
      savePaperIfDirty(state),
    ]);

    // Check for errors
    const failedResults = saveResults.filter((r): r is { success: false; error: { message: string } } => !r.success);
    if (failedResults.length > 0) {
      throw new Error(`Failed to save: ${failedResults.map(e => e.error.message).join(', ')}`);
    }

    // Mark all resources as clean (since we just saved)
    dispatch(markResourceClean('reading'));
    dispatch(markResourceClean('canvas'));
    dispatch(markResourceClean('paper'));

    // Update selection
    dispatch(setSelectedReading(readingId));
    dispatch(setSelectedCanvas(null));
    dispatch(setSelectedPaper(null));

    // Best-effort hydration of the rubric AND per-user annotations for
    // the newly-selected reading. Fire-and-forget — neither should
    // block selection if the request hangs.
    if (readingId && state.workspace.workspace_id) {
      loadRubricForReading(state.workspace.workspace_id, readingId, dispatch);
      loadAnnotationsForReading(state.workspace.workspace_id, readingId, dispatch);
    }

    return readingId;
  }
);

export const hydrateCurrentReading = createAsyncThunk<
  void,
  string,
  { state: RootState }
>(
  'workspace/hydrateCurrentReading',
  async (readingId, { getState, dispatch }) => {
    const state = getState();
    if (!state.workspace.workspace_id) return;
    await loadAnnotationsForReading(state.workspace.workspace_id, readingId, dispatch);
  }
);

/**
 * Stage 6.7: hydrate the per-user canvas blob for the given canvas.
 * WorkspaceDataFetcher dispatches this on auto-select (which uses raw
 * setSelectedCanvas, bypassing the selectCanvas thunk) so the
 * contributor's canvas_data isn't left undefined — the addRectangle
 * reducer no-ops on undefined canvas_data, which silently dropped
 * drag-to-canvas before this thunk was wired in.
 */
export const hydrateCurrentCanvas = createAsyncThunk<
  void,
  string,
  { state: RootState }
>(
  'workspace/hydrateCurrentCanvas',
  async (canvasId, { getState, dispatch }) => {
    const state = getState();
    if (!state.workspace.workspace_id) return;

    // Flush any pending local edits BEFORE fetching from the backend.
    // Without this, a refresh while dirtyResources.canvas is true
    // overwrites the fresh local state (e.g., a just-deleted rect)
    // with stale backend data — the 15s interval save may not have
    // fired yet at unload.
    if (
      state.workspace.dirtyResources.canvas &&
      state.workspace.selectedCanvasId === canvasId
    ) {
      const result = await saveCanvasIfDirty(state);
      if (result.success) {
        dispatch(markResourceClean('canvas'));
      } else {
        console.error('hydrateCurrentCanvas: save before fetch failed:', result.error.message);
      }
    }

    await loadCanvasDataForCanvas(state.workspace.workspace_id, canvasId, dispatch);
  }
);

/**
 * Select a canvas and auto-save the previous resource if dirty
 */
export const selectCanvas = createAsyncThunk<
  string | null,
  string | null,
  { state: RootState }
>(
  'workspace/selectCanvas',
  async (canvasId, { getState, dispatch }) => {
    const state = getState();

    // Save all dirty resources before switching
    const saveResults = await Promise.all([
      saveReadingIfDirty(state),
      saveCanvasIfDirty(state),
      savePaperIfDirty(state),
    ]);

    // Check for errors
    const failedResults = saveResults.filter((r): r is { success: false; error: { message: string } } => !r.success);
    if (failedResults.length > 0) {
      throw new Error(`Failed to save: ${failedResults.map(e => e.error.message).join(', ')}`);
    }

    // Mark all resources as clean (since we just saved)
    dispatch(markResourceClean('reading'));
    dispatch(markResourceClean('canvas'));
    dispatch(markResourceClean('paper'));

    // Update selection
    dispatch(setSelectedCanvas(canvasId));
    dispatch(setSelectedReading(null));
    dispatch(setSelectedPaper(null));

    // Stage 6.2 + 6.7: best-effort hydration of (a) the canvas's
    // rubric (expert snapshot for grading) and (b) the per-user canvas
    // blob. Both fire-and-forget — a slow GET shouldn't block
    // selection. The per-user blob loader replaces the legacy
    // canvases.canvas_data field that setCanvases used to populate.
    // The canvas-data hydrate is ALSO triggered from
    // WorkspaceDataFetcher on auto-select; this call covers manual
    // resource switches.
    if (canvasId && state.workspace.workspace_id) {
      loadRubricForCanvas(state.workspace.workspace_id, canvasId, dispatch);
      loadCanvasDataForCanvas(state.workspace.workspace_id, canvasId, dispatch);
    }

    return canvasId;
  }
);

/**
 * Select a paper and auto-save the previous resource if dirty
 */
export const selectPaper = createAsyncThunk<
  string | null,
  string | null,
  { state: RootState }
>(
  'workspace/selectPaper',
  async (paperId, { getState, dispatch }) => {
    const state = getState();

    // Save all dirty resources before switching
    const saveResults = await Promise.all([
      saveReadingIfDirty(state),
      saveCanvasIfDirty(state),
      savePaperIfDirty(state),
    ]);

    // Check for errors
    const failedResults = saveResults.filter((r): r is { success: false; error: { message: string } } => !r.success);
    if (failedResults.length > 0) {
      throw new Error(`Failed to save: ${failedResults.map(e => e.error.message).join(', ')}`);
    }

    // Mark all resources as clean (since we just saved)
    dispatch(markResourceClean('reading'));
    dispatch(markResourceClean('canvas'));
    dispatch(markResourceClean('paper'));

    // Update selection
    dispatch(setSelectedPaper(paperId));
    dispatch(setSelectedReading(null));
    dispatch(setSelectedCanvas(null));

    return paperId;
  }
);

/**
 * Clear all selections and auto-save any dirty resources
 */
export const clearSelections = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'workspace/clearSelections',
  async (_, { getState, dispatch }) => {
    const state = getState();

    // Save all dirty resources before clearing
    const saveResults = await Promise.all([
      saveReadingIfDirty(state),
      saveCanvasIfDirty(state),
      savePaperIfDirty(state),
    ]);

    // Check for errors
    const failedResults = saveResults.filter((r): r is { success: false; error: { message: string } } => !r.success);
    if (failedResults.length > 0) {
      throw new Error(`Failed to save: ${failedResults.map(e => e.error.message).join(', ')}`);
    }

    // Mark all resources as clean (since we just saved)
    dispatch(markResourceClean('reading'));
    dispatch(markResourceClean('canvas'));
    dispatch(markResourceClean('paper'));

    // Clear selections
    dispatch(clearAllSelections());
  }
);

/**
 * Manually trigger save for the current reading
 */
export const saveCurrentReading = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'workspace/saveCurrentReading',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const result = await saveReadingIfDirty(state);

    if (!result.success) {
      throw new Error(`Failed to save reading: ${result.error.message}`);
    }

    dispatch(markResourceClean('reading'));
  }
);

/**
 * Manually trigger save for the current canvas
 */
export const saveCurrentCanvas = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'workspace/saveCurrentCanvas',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const result = await saveCanvasIfDirty(state);

    if (!result.success) {
      throw new Error(`Failed to save canvas: ${result.error.message}`);
    }

    dispatch(markResourceClean('canvas'));
  }
);

/**
 * Manually trigger save for the current paper
 */
export const saveCurrentPaper = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'workspace/saveCurrentPaper',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const result = await savePaperIfDirty(state);

    if (!result.success) {
      throw new Error(`Failed to save paper: ${result.error.message}`);
    }

    dispatch(markResourceClean('paper'));
  }
);

// ============================================================================
// Grading view (Stage 3.7) — workspace-owner-only flow for viewing a
// specific contributor's annotations against the expert reference.
// ============================================================================

/**
 * Fetch the contributor list for the currently selected reading and
 * cache it on the reading. Idempotent — call freely; the cache is
 * read-through. Owner-only on the backend; non-owners get a 403 here,
 * which we surface as an error returned by the thunk (the UI hides the
 * picker for non-owners anyway).
 */
export const loadReadingContributors = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'gradingView/loadReadingContributors',
  async (_, { getState, dispatch }) => {
    const { workspace_id, selectedReadingId } = getState().workspace;
    if (!workspace_id || !selectedReadingId) return;
    const result = await getReadingContributors(workspace_id, selectedReadingId);
    if (!result.success) {
      console.error('loadReadingContributors failed:', result.error.message);
      return;
    }
    dispatch(setReadingContributorsCache({
      readingId: selectedReadingId,
      contributors: result.data,
    }));
  }
);

export const loadCanvasContributors = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'gradingView/loadCanvasContributors',
  async (_, { getState, dispatch }) => {
    const { workspace_id, selectedCanvasId } = getState().workspace;
    if (!workspace_id || !selectedCanvasId) return;
    const result = await getCanvasContributors(workspace_id, selectedCanvasId);
    if (!result.success) {
      console.error('loadCanvasContributors failed:', result.error.message);
      return;
    }
    dispatch(setCanvasContributorsCache({
      canvasId: selectedCanvasId,
      contributors: result.data,
    }));
  }
);

/**
 * Stage 6.8: enter grading view for a contributor. Picker selection
 * is set at the workspace level (shared across reader and canvas);
 * the per-resource data caches are populated for whichever resource
 * is currently selected. Switching the toggle later picks up the
 * other resource's data via hydrateGradingViewForCurrent* below.
 */
export const enterGradingView = createAsyncThunk<
  void,
  { userId: string; username: string },
  { state: RootState }
>(
  'gradingView/enter',
  async ({ userId, username }, { getState, dispatch }) => {
    const { workspace_id, selectedReadingId, selectedCanvasId } = getState().workspace;
    if (!workspace_id) return;

    dispatch(setGradingView({ userId, username }));

    if (selectedReadingId) {
      const result = await getAnnotations(workspace_id, selectedReadingId, userId);
      if (result.success) {
        dispatch(setReadingGradingViewData({
          readingId: selectedReadingId,
          highlights: annotationsToHighlights(result.data),
        }));
      } else {
        console.error('enterGradingView annotation fetch failed:', result.error.message);
      }
    }

    if (selectedCanvasId) {
      const result = await getCanvasUserData(workspace_id, selectedCanvasId, userId);
      if (result.success) {
        let canvasData: CanvasData = { rectangles: [], scale: 1 };
        if (result.data?.canvas_data) {
          try {
            canvasData = JSON.parse(result.data.canvas_data) as CanvasData;
          } catch (err) {
            console.error('enterGradingView canvas_data parse failed:', err);
          }
        }
        dispatch(setCanvasGradingViewData({
          canvasId: selectedCanvasId,
          canvasData,
        }));
      } else {
        console.error('enterGradingView canvas fetch failed:', result.error.message);
      }
    }
  }
);

/**
 * Stage 6.8: triggered from WorkspaceDataFetcher when grading view is
 * active and the selected reading changes — fetches the contributor's
 * annotations for the newly-selected reading so the cache is current.
 */
export const hydrateGradingViewForCurrentReading = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'gradingView/hydrateReading',
  async (_, { getState, dispatch }) => {
    const { workspace_id, selectedReadingId, gradingView } = getState().workspace;
    if (!workspace_id || !selectedReadingId || !gradingView) return;
    const result = await getAnnotations(workspace_id, selectedReadingId, gradingView.userId);
    if (!result.success) {
      console.error('hydrateGradingViewForCurrentReading failed:', result.error.message);
      return;
    }
    dispatch(setReadingGradingViewData({
      readingId: selectedReadingId,
      highlights: annotationsToHighlights(result.data),
    }));
  }
);

export const hydrateGradingViewForCurrentCanvas = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'gradingView/hydrateCanvas',
  async (_, { getState, dispatch }) => {
    const { workspace_id, selectedCanvasId, gradingView } = getState().workspace;
    if (!workspace_id || !selectedCanvasId || !gradingView) return;
    const result = await getCanvasUserData(workspace_id, selectedCanvasId, gradingView.userId);
    if (!result.success) {
      console.error('hydrateGradingViewForCurrentCanvas failed:', result.error.message);
      return;
    }
    let canvasData: CanvasData = { rectangles: [], scale: 1 };
    if (result.data?.canvas_data) {
      try {
        canvasData = JSON.parse(result.data.canvas_data) as CanvasData;
      } catch (err) {
        console.error('hydrateGradingViewForCurrentCanvas parse failed:', err);
      }
    }
    dispatch(setCanvasGradingViewData({ canvasId: selectedCanvasId, canvasData }));
  }
);

export const exitGradingView = createAsyncThunk<void, void, { state: RootState }>(
  'gradingView/exit',
  async (_, { getState, dispatch }) => {
    dispatch(clearGradingView());

    // Re-hydrate the owner's own resources after exit so memoized
    // selectors (selectCanvasDiagnostics, selectViewedRectangles)
    // don't return stale results from the grading-view computation
    // when the owner's underlying arrays reference-equal what they
    // were before entry. Hydrate dispatches setCanvasData /
    // setReadingHighlights with the backend values, which forces the
    // selectors downstream to recompute against fresh references.
    const { workspace_id, selectedCanvasId, selectedReadingId } = getState().workspace;
    if (!workspace_id) return;
    if (selectedCanvasId) dispatch(hydrateCurrentCanvas(selectedCanvasId));
    if (selectedReadingId) dispatch(hydrateCurrentReading(selectedReadingId));
  }
);

// ============================================================================
// Assessment auto-save (Item 7, Stage 5)
//
// The contributor's client snapshots their grade into reading_assessments
// on a debounced cadence whenever the underlying state changes (their
// highlights or the rubric settings). The thunk reads the current grade
// from state, builds the payload, and PUTs. No-op when:
//   - no reading/workspace selected
//   - no expert reference exists for the reading (no grade to save)
//   - the owner is in grading view (they shouldn't auto-save another
//     user's record from their own session)
//   - the current user is the workspace owner viewing their own reading
//     (they'd just be saving a self-vs-self perfect score — not useful)
// ============================================================================

export const saveReadingAssessment = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'assessment/saveReadingAssessment',
  async (_, { getState, dispatch }) => {
    const state = getState();
    if (isDemoMode(state)) return;
    const { workspace_id, selectedReadingId } = state.workspace;
    if (!workspace_id || !selectedReadingId) return;

    const reading = state.workspace.readings.find(
      (r) => r.reading_id === selectedReadingId
    );
    if (!reading) return;

    // Guard: skip when owner is in grading view (different user's data
    // is on screen) or there's no rubric (no grade to compute).
    if (reading.gradingView) return;
    if (!reading.rubric || reading.rubric.expertHighlights.length === 0) return;

    // Guard: workspace owner doesn't auto-save their own assessment.
    // The owner-of-self case yields a trivial perfect grade.
    const isOwner = (state.workspace.workspace_permissions ?? []).some(
      (p) => p.user_id === state.user.userId && p.permission_name === 'owner'
    );
    if (isOwner) return;

    const highlights = reading.reading_data?.highlights ?? [];
    if (highlights.length === 0) return;

    // Compute the grade body from current state. The selector logic
    // would duplicate here — instead, build a minimal explanation
    // snapshot of the same shape and let the backend store it as JSON.
    const settings = reading.rubric.settings;
    const expert = reading.rubric.expertHighlights;
    const { assessHighlights } = await import(
      '@features/annogram/sidebar/utils/assessmentChecks'
    );
    const thresholdFraction = settings.matchThreshold / 100;
    const assessment = assessHighlights(highlights, expert, thresholdFraction);
    const fractionalMissed = expert.length - assessment.totalCredit;

    const completenessScore = settings.completeness.enabled
      ? Math.max(0, 100 - settings.completeness.penalty * fractionalMissed)
      : null;
    const categoryAccuracyScore = settings.categoryMismatch.enabled
      ? Math.max(0, 100 - settings.categoryMismatch.penalty * assessment.miscategorizedCount)
      : null;
    const enabledScores = [completenessScore, categoryAccuracyScore].filter(
      (s): s is number => s !== null
    );
    const total = enabledScores.length > 0
      ? Math.round(enabledScores.reduce((a, b) => a + b, 0) / enabledScores.length)
      : null;

    const explanation = JSON.stringify({
      matchThreshold: settings.matchThreshold,
      completeness: {
        score: completenessScore !== null ? Math.round(completenessScore) : null,
        missedCount: fractionalMissed,
      },
      categoryAccuracy: {
        score: categoryAccuracyScore !== null ? Math.round(categoryAccuracyScore) : null,
        miscategorizedCount: assessment.miscategorizedCount,
      },
      coverageQuality: assessment.totalCredit / expert.length,
      matchedCount: assessment.matchedCount,
      expertCount: expert.length,
      savedAt: Date.now(),
    });

    const result = await updateReadingAssessment(workspace_id, selectedReadingId, {
      points: total,
      explanation,
      student_coords: JSON.stringify(highlights),
    });
    if (result.success) {
      dispatch(markAssessmentSaved({
        readingId: selectedReadingId,
        savedAt: Date.now(),
      }));
    } else {
      console.error('saveReadingAssessment failed:', result.error.message);
    }
  }
);

// ============================================================================
// Canvas assessment auto-save (Item 7, Stage 6.6)
//
// Mirrors saveReadingAssessment. Computes the canvas grade body from
// state via assessCanvas and PUTs to /assessment. Guards: in grading
// view (read-only), workspace owner (self-vs-self trivial), no expert
// canvas reference, empty student canvas — all no-op early.
// ============================================================================

export const saveCanvasAssessment = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'assessment/saveCanvasAssessment',
  async (_, { getState, dispatch }) => {
    const state = getState();
    if (isDemoMode(state)) return;
    const { workspace_id, selectedCanvasId } = state.workspace;
    if (!workspace_id || !selectedCanvasId) return;

    const canvas = state.workspace.canvases.find(
      (c) => c.canvas_id === selectedCanvasId
    );
    if (!canvas || !canvas.canvas_data) return;

    // Skip in grading view (different user's canvas on screen) and
    // when no expert reference is saved.
    if (!canvas.rubric?.expertCanvas) return;
    // Reading uses gradingView for cross-user viewing; canvas doesn't
    // have a parallel state slot, but the workspace_id+selected match
    // is implicitly per-user via the JWT.

    // Workspace owner doesn't auto-save their own canvas assessment
    // (self-vs-self trivial perfect grade).
    const isOwner = (state.workspace.workspace_permissions ?? []).some(
      (p) => p.user_id === state.user.userId && p.permission_name === 'owner'
    );
    if (isOwner) return;

    if (canvas.canvas_data.rectangles.length === 0) return;

    const { assessCanvas } = await import(
      '@features/annogram/canvas/utils/assessCanvas'
    );
    const expert = canvas.rubric.expertCanvas.rectangles;
    const result = assessCanvas(canvas.canvas_data.rectangles, expert);

    // Same 5 / 3 / 5 penalty defaults as selectCanvasGrade — kept in
    // sync here rather than imported to avoid coupling the thunk to a
    // selectors-package internal.
    const nodeScore = Math.max(0, 100 - 5 * result.missingNodeCount);
    const categoryScore = Math.max(0, 100 - 3 * result.miscategorizedCount);
    const edgeScore = Math.max(0, 100 - 5 * result.missingEdgeCount);
    const total = Math.round((nodeScore + categoryScore + edgeScore) / 3);

    const explanation = JSON.stringify({
      nodeCoverage: { score: Math.round(nodeScore), missing: result.missingNodeCount },
      categoryAccuracy: {
        score: Math.round(categoryScore),
        miscategorized: result.miscategorizedCount,
      },
      edgeCoverage: { score: Math.round(edgeScore), missing: result.missingEdgeCount },
      matchedNodes: result.matchedNodeCount,
      expertNodes: expert.length,
      matchedEdges: result.matchedEdgeCount,
      expertEdges: result.edgeOutcomes.length,
      orphanStudents: result.orphanStudentCount,
      duplicates: result.duplicateStudentCount,
      savedAt: Date.now(),
    });

    const r = await updateCanvasAssessment(workspace_id, selectedCanvasId, {
      points: total,
      explanation,
      student_coords: JSON.stringify(canvas.canvas_data),
    });
    if (r.success) {
      dispatch(markCanvasAssessmentSaved({
        canvasId: selectedCanvasId,
        savedAt: Date.now(),
      }));
    } else {
      console.error('saveCanvasAssessment failed:', r.error.message);
    }
  }
);

/**
 * Save all dirty resources (useful for "Save All" button or before logout)
 */
export const saveAllDirtyResources = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(
  'workspace/saveAllDirtyResources',
  async (_, { getState, dispatch }) => {
    const state = getState();

    const saveResults = await Promise.all([
      saveReadingIfDirty(state),
      saveCanvasIfDirty(state),
      savePaperIfDirty(state),
    ]);

    // Check for errors
    const failedResults = saveResults.filter((r): r is { success: false; error: { message: string } } => !r.success);
    if (failedResults.length > 0) {
      throw new Error(`Failed to save: ${failedResults.map(e => e.error.message).join(', ')}`);
    }

    // Mark all resources as clean
    dispatch(markResourceClean('reading'));
    dispatch(markResourceClean('canvas'));
    dispatch(markResourceClean('paper'));
  }
);
